import { useDispatch, useSelector } from "react-redux";
import { GetProposalOwnersParams, SearchFieldProposalOwnership, useGetProposalOfUserQuery, useGetUsersOfGMSQuery } from "../../api/gms/transfer-ownership/transfer-ownership-api";
import { selectAllProposalOfUserPagination, setPagination } from "../../features/pagination/pagination-slice";
import { getErrorMessage } from "../../utils/get-error-message.utils";
import { options } from "../../data/group-insurance";
import { useEffect, useMemo } from "react";
import { DropdownOption } from "../../features/auth/auth-state";
import { totalPages, TotalPages } from "../../features/pagination/pagination-inital-state";
import { useTransferOwnershipDataState } from "../data-state/use-transferOwnership-data-state";
import { skipToken } from "@reduxjs/toolkit/query";
import { GMSUsers } from "../../api/gms/transfer-ownership/transfer-ownership-response";

export const useTransferOwnershipAccess = () => {
    const dispatch = useDispatch();
    // const userId = 32
    const {
        currentOwnerId,
        ownershipSearchValue,
    } = useTransferOwnershipDataState()
    // const { currentOwnerId: userId } = useTransferOwnershipDataState()
    const {
        searchType,
        searchValue,
        page,
        defaultTotalPages,
        isTyping,
        isPageSizeChanging,
        isDebounceDone,
    } = useSelector(selectAllProposalOfUserPagination);

    const selectedSearchType = searchType ? { id: searchType.id, name: searchType.name } : null;

    const setSelectedSearchType = (type: { id: number; name: string } | null) => {
        if (!type) {
            dispatch(setPagination({
                type: "allProposalOfUser",
                value: { searchType: null, searchValue: "", page: 1, },
            }));
            return;
        }

        const option = options.find((option) => option.id === type.id) ?? null;

        dispatch(setPagination({
            type: "allProposalOfUser",
            value: { searchType: option, searchValue: "", page: 1, },
        }));
    };

    const setSearchValue = (value: string) => {
        dispatch(setPagination({
            type: "allProposalOfUser",
            value: { searchValue: value, page: 1, },
        }));
    };

    const setPage = (value: number) => {
        dispatch(setPagination({
            type: "allProposalOfUser",
            value: { page: value, },
        }));
    };

    const params: GetProposalOwnersParams | typeof skipToken =
        currentOwnerId !== null && currentOwnerId > 0
            ? {
                userId: currentOwnerId,
                page,
                per_page: defaultTotalPages.value,
                ...(searchType && searchValue.trim()
                    ? {
                            [searchType.value as SearchFieldProposalOwnership]:
                                searchValue.trim(),
                        }
                    : {}),
            }
            : skipToken;

    const {
        data: proposals,
        isLoading: isLoadingProposals,
        isError: isErrorProposals,
        error: errorProposals,
        refetch: refetchProposals,
        isFetching: isFetchingProposals,
    } = useGetProposalOfUserQuery(params);
    const errorMessageProposals = getErrorMessage(errorProposals);

    const availableProposals = useMemo(() => {
        if (currentOwnerId === 0 || currentOwnerId === null) {
            return [];
        }

        const searchValue = ownershipSearchValue.trim().toLowerCase();

        return (proposals?.data ?? []).filter((proposal) => {
            const matchesSearch =
                !searchValue ||
                proposal.group_name?.toLowerCase().includes(searchValue) ||
                proposal.basic_plan?.name?.toLowerCase().includes(searchValue) ||
                proposal.proposal_type?.name?.toLowerCase().includes(searchValue) ||
                proposal.status?.name?.toLowerCase().includes(searchValue);

            return matchesSearch;
        });
    }, [currentOwnerId, ownershipSearchValue, proposals]);

    /** Handle search debounce */
    useEffect(() => {
        dispatch(setPagination({
            type: "allProposalOfUser",
            value: { isTyping: true, isDebounceDone: false, },
        }));

        const timer = setTimeout(() => {
            dispatch(setPagination({
                type: "allProposalOfUser",
                value: { isDebounceDone: true, },
            }));
        }, 300);

        return () => clearTimeout(timer);
    }, [searchValue, dispatch]);

    /** Stop typing state after debounce and API request has finished */
    useEffect(() => {
        if (isDebounceDone && !isFetchingProposals) {
            dispatch(setPagination({
                type: "allProposalOfUser",
                value: { isTyping: false, },
            }));
        }
    }, [
        isDebounceDone,
        isFetchingProposals,
        dispatch,
    ]);

    /** Stop page-size loading state after API request has finished */
    useEffect(() => {
        if (isPageSizeChanging && !isFetchingProposals) {
            dispatch(setPagination({
                type: "allProposalOfUser",
                value: { isPageSizeChanging: false, },
            }));
        }
    }, [
        isPageSizeChanging,
        isFetchingProposals,
        dispatch,
    ]);

    /** Handle page-size change */
    const handleChangePageSize = (value: TotalPages) => {
        dispatch(setPagination({
            type: "allProposalOfUser",
            value: { defaultTotalPages: value, page: 1, isPageSizeChanging: true, },
        }));
    };

    /** Handle reload */
    const handleReload = () => {
        dispatch(setPagination({
            type: "allProposalOfUser",
            value: {page: 1},
        }));
        refetchProposals();
    };

    /** Combined loading state */
    const loading = isTyping || isLoadingProposals || isPageSizeChanging || isFetchingProposals;

    const normalizedOptions: DropdownOption[] = options.map(
        (option) => ({ id: option.id, name: option.name, })
    );

    // const {
    //     data: gmsUsers,
    //     isLoading: isLoadingGMSUsers,
    //     isError: isErrorGMSUsers,
    //     error: errorGMSUsers,
    //     refetch: refetchGMSUsers,
    //     isFetching: isFetchingGMSUsers,
    // } = useGetUsersOfGMSQuery();
    // const errorMessageGMSUsers = getErrorMessage(errorGMSUsers);

    // const formattedGMSUsers = useMemo(() => (gmsUsers?.users ?? []).map((user: GMSUsers) => ({
    //     id: user.user_id,
    //     name: user.name,
    //     email: user.email,
    // })), [gmsUsers?.users]);

    // const formattedGMSUsersInActive: DropdownOption[] = useMemo(() => (gmsUsers?.users ?? [])
    //     .filter((user: GMSUsers) => user.is_active === false)
    //     .map((user: GMSUsers) => ({
    //         id: user.user_id,
    //         name: user.name,
    //         subTitle: user.email,
    // })), [gmsUsers?.users]);

    // const formattedGMSUsersActive: DropdownOption[] = useMemo(() => (gmsUsers?.users ?? [])
    //     .filter((user: GMSUsers) => user.is_active === true)
    //     .map((user: GMSUsers) => ({
    //         id: user.user_id,
    //         name: user.name,
    //         subTitle: user.email,
    // })), [gmsUsers?.users]);



    return {
        proposals,
        isLoadingProposals,
        isErrorProposals,
        errorProposals,
        errorMessage: errorMessageProposals,
        refetchProposals,
        isFetchingProposals,

        normalizedOptions,
        searchType: selectedSearchType,
        setSearchType: setSelectedSearchType,
        searchValue,
        setSearchValue,
        setPage,

        // pagedData: proposals?.data ?? [],
        pagedData: availableProposals ?? [],
        loading,
        handleReload,
        defaultTotalPages,
        setDefaultTotalPages: handleChangePageSize,
        proposalDefaultPageSize: totalPages,
        handleChangePageSize,

        // gmsUsers: gmsUsers?.users ?? [],
        // isLoadingGMSUsers,
        // isErrorGMSUsers,
        // errorGMSUsers,
        // errorMessageGMSUsers,
        // refetchGMSUsers,
        // formattedGMSUsers,
        // isFetchingGMSUsers,

        // formattedGMSUsersInActive,
        // formattedGMSUsersActive,
    };
};